/**
 * Cookie utility for storing user preferences
 */

export interface UserPreferences {
  isMuted: boolean;
  isDarkMode: boolean;
  isInfinityMode: boolean;
  gridSize: 4 | 6 | 8;
}

const COOKIE_NAME = 'snap-pairs-preferences';
const COOKIE_EXPIRY_DAYS = 365;

/**
 * Set a cookie with expiration
 */
function setCookie(name: string, value: string, days: number): void {
  const date = new Date();
  date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
  const expires = `expires=${date.toUTCString()}`;
  document.cookie = `${name}=${value};${expires};path=/;SameSite=Lax`;
}

/**
 * Get a cookie by name
 */
function getCookie(name: string): string | null {
  const nameEQ = `${name}=`;
  const cookies = document.cookie.split(';');

  for (let i = 0; i < cookies.length; i++) {
    let cookie = cookies[i].trim();
    if (cookie.indexOf(nameEQ) === 0) {
      return cookie.substring(nameEQ.length);
    }
  }

  return null;
}

/**
 * Save user preferences to cookie
 */
export function savePreferences(preferences: UserPreferences): void {
  const json = JSON.stringify(preferences);
  setCookie(COOKIE_NAME, json, COOKIE_EXPIRY_DAYS);
}

/**
 * Load user preferences from cookie
 */
export function loadPreferences(): UserPreferences | null {
  const cookieValue = getCookie(COOKIE_NAME);

  if (!cookieValue) {
    return null;
  }

  try {
    const preferences = JSON.parse(cookieValue) as UserPreferences;
    return preferences;
  } catch (error) {
    console.error('Failed to parse preferences cookie:', error);
    return null;
  }
}

/**
 * Get default preferences
 */
export function getDefaultPreferences(): UserPreferences {
  return {
    isMuted: false,
    isDarkMode: false,
    isInfinityMode: false,
    gridSize: 6
  };
}
